import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import bankAccountsService from '../services/bankAccountsService';
import '../assets/bankAccounts.css';

const BankAccounts = () => {
  const navigate = useNavigate();

  // State for main data
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for DataTable controls
  const [searchTerm, setSearchTerm] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  
  // State for column visibility
  const [visibleColumns, setVisibleColumns] = useState({
    name: true,
    actions: true
  });
  
  // State for column visibility dropdown
  const [showColumnDropdown, setShowColumnDropdown] = useState(false);

  // Load bank accounts on component mount
  useEffect(() => {
    loadBankAccounts();
  }, []);

  const loadBankAccounts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await bankAccountsService.getBankAccounts();
      
      if (response.success) {
        setBankAccounts(response.data);
      } else {
        throw new Error(response.message || 'Failed to load bank accounts');
      }
    } catch (err) {
      console.error('Error loading bank accounts:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Filter and paginate data
  const filteredBankAccounts = bankAccounts.filter(account =>
    account.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredBankAccounts.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedData = filteredBankAccounts.slice(startIndex, startIndex + pageSize);

  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Handle page size change
  const handlePageSizeChange = (e) => {
    setPageSize(parseInt(e.target.value));
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Handle pagination
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Navigation functions
  const handleAddClick = () => {
    navigate('/bank-accounts/create');
  };

  const handleEditClick = (accountId) => {
    console.log(`Navigating to edit page for bank account ${accountId}`);
    navigate(`/bank-accounts/${accountId}/edit`);
  };

  // Handle export functions
  const handleExportCSV = () => {
    bankAccountsService.exportCSV();
  };

  const handleExportPDF = () => {
    bankAccountsService.exportPDF();
  };

  const handleColumnVisibility = () => {
    setShowColumnDropdown(!showColumnDropdown);
  };

  const toggleColumn = (columnName) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnName]: !prev[columnName]
    }));
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showColumnDropdown && !event.target.closest('.column-visibility-dropdown')) {
        setShowColumnDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColumnDropdown]);

  // Render pagination
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <li key={i} className={currentPage === i ? 'active' : ''}>
          <button 
            className="btn-link" 
            onClick={() => handlePageChange(i)}
          >
            {i}
          </button>
        </li>
      );
    }

    return (
      <div className="dataTables_paginate">
        <ul className="pagination">
          <li className={currentPage === 1 ? 'disabled' : ''}>
            <button 
              className="btn-link" 
              onClick={() => { if (currentPage > 1) handlePageChange(currentPage - 1); }}
              disabled={currentPage === 1}
            >
              Previous
            </button>
          </li>
          {pages}
          <li className={currentPage === totalPages ? 'disabled' : ''}>
            <button 
              className="btn-link" 
              onClick={() => { if (currentPage < totalPages) handlePageChange(currentPage + 1); }}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </li>
        </ul>
      </div>
    );
  };

  return (
    <div className="bank-accounts-container">
      <div className="container-fluid">
        {/* Breadcrumb */}
        <nav className="breadcrumb">
          <span className="breadcrumb-item">
            <a href="/dashboard">Dashboard</a>
          </span>
          <span className="breadcrumb-item active">GG Bank Accounts</span>
        </nav>

        <div className="row">
          <div className="col-md-12">
            <div className="panel panel-default panel-standard">
              {/* Panel Header */}
              <div className="panel-heading panel-title">
                <h3 className="mr-auto">GG Bank Accounts</h3>
                <button className="btn btn-secondary" onClick={handleAddClick}>
                  <i className="glyphicon glyphicon-plus"></i> Add GG Bank Account
                </button>
              </div>

              {/* Panel Body */}
              <div className="panel-body">
                {loading ? (
                  <div className="loading">Loading bank accounts...</div>
                ) : error ? (
                  <div className="error-message">
                    <p>{error}</p>
                    <button className="btn btn-primary" onClick={loadBankAccounts}>
                      Try Again
                    </button>
                  </div>
                ) : bankAccounts.length > 0 ? (
                  <div className="dataTable_wrapper">
                    {/* DataTable Toolbar */}
                    <div className="dt-toolbar">
                      {/* Length Control */}
                      <div className="dataTables_length">
                        <label>
                          Show 
                          <select value={pageSize} onChange={handlePageSizeChange}>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={75}>75</option>
                            <option value={100}>100</option>
                          </select>
                          entries
                        </label>
                      </div>

                      {/* Export Buttons */}
                      <div className="dt-buttons">
                        <button className="dt-button" onClick={handleExportCSV}>
                          Export as CSV
                        </button>
                        <button className="dt-button" onClick={handleExportPDF}>
                          Export as PDF
                        </button>
                        <div className="column-visibility-dropdown">
                          <button className="dt-button" onClick={handleColumnVisibility}>
                            Column visibility
                          </button>
                          {showColumnDropdown && (
                            <div className="column-visibility-menu">
                              <div className="column-visibility-item">
                                <label>
                                  <input
                                    type="checkbox"
                                    checked={visibleColumns.name}
                                    onChange={() => toggleColumn('name')}
                                  />
                                  Name
                                </label>
                              </div>
                              <div className="column-visibility-item">
                                <label>
                                  <input
                                    type="checkbox"
                                    checked={visibleColumns.actions}
                                    onChange={() => toggleColumn('actions')}
                                  />
                                  Actions
                                </label>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Search Filter */}
                      <div className="dataTables_filter">
                        <label>
                          Search:
                          <input
                            type="text"
                            value={searchTerm}
                            onChange={handleSearch}
                            placeholder="Search bank accounts..."
                          />
                        </label>
                      </div>
                    </div>

                    {/* Data Table */}
                    <div className="table-responsive">
                      <table className="table table-hover dt-responsive">
                        <thead>
                          <tr>
                            {visibleColumns.name && <th>Name</th>}
                            {visibleColumns.actions && <th className="action-head">Actions</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedData.map((account) => (
                            <tr key={account.id}>
                              {visibleColumns.name && <td>{account.name}</td>}
                              {visibleColumns.actions && (
                                <td className="action">
                                  <button
                                    className="btn btn-primary btn-sm"
                                    onClick={() => handleEditClick(account.id)}
                                  >
                                    Edit
                                  </button>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* DataTable Footer */}
                    <div className="row">
                      <div className="col-sm-5">
                        <div className="dataTables_info">
                          Showing {startIndex + 1} to {Math.min(startIndex + pageSize, filteredBankAccounts.length)} of {filteredBankAccounts.length} entries
                        </div>
                      </div>
                      <div className="col-sm-7">
                        {renderPagination()}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="no-data">
                    No GG bank accounts!
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Add Bank Account Modal - Removed since we're using dedicated page */}
      </div>
    </div>
  );
};

export default BankAccounts;